import { KioskKeypad } from "./KioskKeypad";

export type ReturningMode = "phone" | "email" | "name";

type KioskReturningPanelProps = {
  mode: ReturningMode;
  onModeChange: (mode: ReturningMode) => void;
  phone: string;
  onPhoneChange: (value: string) => void;
  email: string;
  onEmailChange: (value: string) => void;
  name: string;
  onNameChange: (value: string) => void;
  busy: boolean;
  disabled: boolean;
  error: string | null;
  noMatch: boolean;
  onCheckIn: () => void;
  onGoRegister: () => void;
};

function formatPhoneDisplay(digits: string): string {
  if (!digits) return "Enter your mobile";
  if (digits.length <= 3) return digits;
  if (digits.length <= 6) return `${digits.slice(0, 3)} ${digits.slice(3)}`;
  if (digits.length <= 10) {
    return `${digits.slice(0, 3)} ${digits.slice(3, 6)} ${digits.slice(6)}`;
  }
  return `${digits.slice(0, 3)} ${digits.slice(3, 6)} ${digits.slice(6, 10)} ${digits.slice(10)}`;
}

export function KioskReturningPanel({
  mode,
  onModeChange,
  phone,
  onPhoneChange,
  email,
  onEmailChange,
  name,
  onNameChange,
  busy,
  disabled,
  error,
  noMatch,
  onCheckIn,
  onGoRegister,
}: KioskReturningPanelProps) {
  const canSubmit =
    !busy &&
    !disabled &&
    ((mode === "phone" && phone.length >= 7) ||
      (mode === "email" && email.trim().includes("@")) ||
      (mode === "name" && name.trim().length >= 2));

  return (
    <div className="kiosk-panel">
      <div className="kiosk-segment" role="tablist" aria-label="Lookup method">
        {(
          [
            ["phone", "Phone"],
            ["email", "Email"],
            ["name", "Name"],
          ] as const
        ).map(([id, label]) => (
          <button
            key={id}
            type="button"
            role="tab"
            aria-selected={mode === id}
            className={`kiosk-segment-btn${mode === id ? " is-active" : ""}`}
            onClick={() => onModeChange(id)}
            disabled={busy}
          >
            {label}
          </button>
        ))}
      </div>

      {mode === "phone" && (
        <div className="kiosk-phone-block">
          <div
            className={`kiosk-phone-display${phone ? "" : " is-placeholder"}`}
            aria-live="polite"
          >
            {formatPhoneDisplay(phone)}
          </div>
          <KioskKeypad value={phone} onChange={onPhoneChange} disabled={busy || disabled} />
        </div>
      )}

      {mode === "email" && (
        <label className="kiosk-field">
          <span>Email</span>
          <input
            className="kiosk-input"
            type="email"
            inputMode="email"
            autoComplete="email"
            autoCapitalize="none"
            value={email}
            onChange={(e) => onEmailChange(e.target.value)}
            placeholder="you@example.com"
            disabled={busy || disabled}
            autoFocus
          />
        </label>
      )}

      {mode === "name" && (
        <label className="kiosk-field">
          <span>Full name</span>
          <input
            className="kiosk-input"
            type="text"
            autoComplete="name"
            value={name}
            onChange={(e) => onNameChange(e.target.value)}
            placeholder="Your full name"
            disabled={busy || disabled}
            autoFocus
          />
        </label>
      )}

      {error && <div className="form-error kiosk-message">{error}</div>}

      {noMatch && (
        <div className="kiosk-nomatch">
          <p>We couldn’t find you. First time here?</p>
          <button type="button" className="btn kiosk-btn kiosk-btn-secondary" onClick={onGoRegister}>
            First time? Register
          </button>
        </div>
      )}

      <button
        type="button"
        className="btn btn-primary kiosk-btn kiosk-btn-primary"
        disabled={!canSubmit}
        onClick={onCheckIn}
      >
        {busy ? "Checking in…" : "Check in"}
      </button>
    </div>
  );
}
